import React from 'react';
import styled from 'styled-components';

import { Text, Button } from '@companycam/slab-web';
import { translate } from 'translations/i18n';
import {
  getAddressComponent,
  GooglePlaceAutocomplete,
} from 'components/shared/GooglePlaceAutocomplete';
import { Row } from '../CommonComponents';
import { type UseProfileFormReturn } from '../useProfileForm';
import { type GooglePlaceDetails } from 'data/v3/data-contracts';
import Steven from 'helpers/steven';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cc_size_spacing_xs);
`;

const ValuePropList = styled.ul`
  margin-left: 20px;
  margin-bottom: var(--cc_size_spacing_l);
  padding: 0;
  list-style-type: circle;
  display: flex;
  flex-direction: column;
  gap: calc(var(--cc_size_spacing_s) + var(--cc_size_spacing_xs));
`;

const ValueProps = () => {
  const valuePropMessages = [
    translate('portfolio.profile.valueProp1'),
    translate('portfolio.profile.valueProp2'),
    translate('portfolio.profile.valueProp3'),
  ];

  return (
    <ValuePropList>
      {valuePropMessages.map((message) => (
        <li key={message}>
          <Text
            ccMargin="none none none s"
            size="m"
            family="heading"
            weight="500"
            as="p"
          >
            {message}
          </Text>
        </li>
      ))}
    </ValuePropList>
  );
};

type GoogleSearchStepProps = {
  formMethods: UseProfileFormReturn;
  nextStep: () => void;
};

export const GoogleSearchStepFooterActions = ({
  nextStep,
}: {
  nextStep: () => void;
}) => {
  const handleContinue = () => {
    Steven.elementClicked({
      id: 'create_public_profile_skip_google_place_search',
      content: 'common.continue',
      type: 'button',
      action: 'create_public_profile_skip_google_place_search_clicked',
    });
    nextStep();
  };

  return (
    <Button color="primary" onClick={handleContinue}>
      {translate('common.continue')}
    </Button>
  );
};

export const GoogleSearchStep = ({
  formMethods,
  nextStep,
}: GoogleSearchStepProps) => {
  const handlePlaceChanged = (place: GooglePlaceDetails) => {
    const details = place.placeDetails;

    formMethods.setValue('name', details.displayName.text);
    formMethods.setValue('googlePlaceId', details.id);
    if (details.websiteUri) {
      formMethods.setValue('website', new URL(details.websiteUri).origin);
    }
    formMethods.setValue('phoneNumber', details.nationalPhoneNumber);

    // address
    formMethods.setValue(
      'addressLine1',
      getAddressComponent(details.addressComponents, [
        'street_number',
        'route',
      ]),
    );
    formMethods.setValue(
      'addressLine2',
      getAddressComponent(details.addressComponents, ['subpremise']),
    );
    formMethods.setValue(
      'city',
      getAddressComponent(details.addressComponents, ['locality']),
    );
    formMethods.setValue(
      'state',
      getAddressComponent(details.addressComponents, [
        'administrative_area_level_1',
      ]),
    );
    formMethods.setValue(
      'postalCode',
      getAddressComponent(details.addressComponents, ['postal_code']),
    );

    Steven.elementClicked({
      id: 'create_public_profile_select_google_place_search',
      content: details.displayName.text,
      type: 'button',
      action: 'create_public_profile_select_google_place_search_clicked',
    });

    nextStep();
  };

  return (
    <Container>
      <div>
        <Text
          as="h1"
          size="xl"
          family="heading"
          weight="800"
          ccMargin="xs none none"
        >
          {translate('portfolio.profile.setupTitle')}
        </Text>
        <Text
          size="m"
          ccMargin="xs none m"
          family="heading"
          weight="500"
          as="h2"
        >
          {translate('portfolio.profile.setupSubtitle')}
        </Text>
      </div>
      <ValueProps />
      <Row>
        <GooglePlaceAutocomplete
          onPlaceSelect={handlePlaceChanged}
          label="Google Business Search"
          placeholder={translate('portfolio.profile.searchGoogle')}
          message={translate('portfolio.profile.searchGoogleMessage')}
        />
      </Row>
    </Container>
  );
};
